/**
 * projects-ui.ts — the project and case browser (P2/US-213).
 *
 * ────────────────────────────────────────────────────────────────────────────────────────────────
 * THE CASE LIST SHOWS THE ENGINE VERSION, AND THAT IS NOT A DEBUG COLUMN
 * ────────────────────────────────────────────────────────────────────────────────────────────────
 * A saved case is a record of what the tool said on a given day (D10). When the engine or the
 * catalog moves, the question an engineer needs to answer about forty stored cases is *which of
 * these predate the change* — and a list that showed only titles and dates would make them open all
 * forty to find out. The column is the cheapest possible answer to the question the phase exists to
 * make answerable, and US-215's banner is the same information at the level of one case.
 *
 * ────────────────────────────────────────────────────────────────────────────────────────────────
 * THE EMPTY STATE TELLS A FIRST-DAY USER WHAT TO DO
 * ────────────────────────────────────────────────────────────────────────────────────────────────
 * "Aucun projet" is true and useless. The first thing a pilot user sees after their invite is this
 * screen with nothing on it, and what they need at that moment is the next action — create a
 * project — not confirmation that the database is empty.
 *
 * ────────────────────────────────────────────────────────────────────────────────────────────────
 * DELETING NAMES WHAT IS BEING DELETED, AND SAYS THE ROW SURVIVES
 * ────────────────────────────────────────────────────────────────────────────────────────────────
 * The confirmation carries the case's own title. A dialog that says "supprimer ce cas ?" next to a
 * list of twelve is a dialog people confirm without reading, and the one time it matters they will
 * have deleted the wrong one. It also says the case is archived rather than destroyed, because that
 * is true (D11, FR-15) and because a user who believes deletion is final behaves differently.
 */

import type { CaseSummary, ProjectResponse } from '@ronceray/contract';

import { escapeHtml } from './dom';

/** `2026-09-05 14:32:10` → `05/09/2026`. The tool writes French dates elsewhere; so does this. */
export function shortDate(sqlTimestamp: string): string {
  const [date] = sqlTimestamp.split(' ');
  const parts = (date ?? '').split('-');
  if (parts.length !== 3) return sqlTimestamp;
  return `${parts[2]}/${parts[1]}/${parts[0]}`;
}

/** The module names as the tabs spell them, so one word means one thing across the tool. */
export const MODULE_LABELS: Record<string, string> = {
  lamine: 'Profiles lamines',
  creux: 'Profils creux',
  bois: 'Bois',
};

export const NO_PROJECTS_MESSAGE =
  'Aucun projet pour le moment. Creez-en un pour enregistrer vos cas de calcul : ils resteront ' +
  'accessibles a toute votre equipe.';

export const NO_CASES_MESSAGE =
  'Aucun cas dans ce projet. Revenez au calcul, saisissez une barre et utilisez ' +
  '"Enregistrer le cas".';

/** The project list, or the empty state. */
export function projectListHTML(projects: readonly ProjectResponse[]): string {
  if (projects.length === 0) return `<p class="vide">${NO_PROJECTS_MESSAGE}</p>`;

  let h = '<table class="opt"><thead><tr><th>Projet</th><th>Adresse</th><th>Creation</th></tr>';
  h += '</thead><tbody>';

  for (const project of projects) {
    h +=
      `<tr><td><button type="button" class="mini" data-project="${escapeHtml(project.id)}">` +
      `${escapeHtml(project.name)}</button></td>` +
      `<td>${escapeHtml(project.address ?? '')}</td>` +
      `<td class="num">${shortDate(project.createdAt)}</td></tr>`;
  }

  return `${h}</tbody></table>`;
}

/**
 * The case list for one project.
 *
 * `deletedAt` never appears: the API excludes soft-deleted cases from this list, and a UI column
 * for a state the list cannot contain would be a promise it does not keep.
 */
export function caseListHTML(cases: readonly CaseSummary[]): string {
  if (cases.length === 0) return `<p class="vide">${NO_CASES_MESSAGE}</p>`;

  let h =
    '<table class="opt"><thead><tr><th>Cas</th><th>Module</th><th>Date</th>' +
    '<th>Moteur / catalogue</th><th></th></tr></thead><tbody>';

  for (const row of cases) {
    const title = row.title ?? '(sans titre)';
    h +=
      `<tr><td><button type="button" class="mini" data-case="${escapeHtml(row.id)}">` +
      `${escapeHtml(title)}</button></td>` +
      `<td>${escapeHtml(MODULE_LABELS[row.module] ?? row.module)}</td>` +
      `<td class="num">${shortDate(row.createdAt)}</td>` +
      `<td class="num">${escapeHtml(row.engineVersion)} / ${escapeHtml(row.catalogVersion)}</td>` +
      `<td><button type="button" class="mini" data-delete="${escapeHtml(row.id)}" ` +
      `data-title="${escapeHtml(title)}">supprimer</button></td></tr>`;
  }

  return `${h}</tbody></table>`;
}

/** The confirmation sentence. Names the case, and says what deletion actually does. */
export function deleteConfirmMessage(title: string): string {
  return (
    `Supprimer le cas "${title}" ?\n\n` +
    "Il disparait des listes mais reste conserve pour l'audit : les cas de calcul ne sont jamais " +
    'effaces definitivement.'
  );
}

/** The whole browser view. One `<h2>` per section, so the two lists are never confused. */
export function browserHTML(options: {
  readonly projects: readonly ProjectResponse[];
  readonly selected?: ProjectResponse | undefined;
  readonly cases?: readonly CaseSummary[] | undefined;
}): string {
  const cases =
    options.selected === undefined
      ? ''
      : `<section class="card p2-cases"><h2>${escapeHtml(options.selected.name)}</h2>
${caseListHTML(options.cases ?? [])}</section>`;

  return `<section class="card p2-projects">
<h2>Projets</h2>
${projectListHTML(options.projects)}
<form class="p2-newproject" id="p2-newproject" onsubmit="return false">
<div class="row"><label for="p2-projectname">Nouveau projet</label>
<input type="text" id="p2-projectname" placeholder="Nom du projet"></div>
<div class="tools"><button type="submit" id="p2-createproject">Creer</button></div>
</form>
</section>
${cases}`;
}

/* ================================================================================================
 * The controller
 * ============================================================================================== */

export interface ProjectsDeps {
  readonly listProjects: () => Promise<{ ok: boolean; value?: { projects: ProjectResponse[] } }>;
  readonly listCases: (
    projectId: string,
  ) => Promise<{ ok: boolean; value?: { cases: CaseSummary[] } }>;
  readonly createProject: (name: string) => Promise<{ ok: boolean }>;
  readonly deleteCase: (caseId: string) => Promise<{ ok: boolean }>;
  readonly openCase: (caseId: string) => void;
  readonly selectProject: (projectId: string | undefined) => void;
  /** `window.confirm` in the application; a stub in a test. */
  readonly confirm: (message: string) => boolean;
}

/**
 * Render and wire the browser.
 *
 * DOM-binding, so the Playwright suite covers it and the unit suite covers the pure renderers
 * above — the same split P1 used for the optimiser panel.
 */
export function projectsView(host: HTMLElement, deps: ProjectsDeps) {
  async function render(selectedId?: string): Promise<void> {
    const listed = await deps.listProjects();
    const projects = listed.ok ? (listed.value?.projects ?? []) : [];
    const selected = projects.find((project) => project.id === selectedId);

    let cases: CaseSummary[] | undefined;
    if (selected !== undefined) {
      const listedCases = await deps.listCases(selected.id);
      cases = listedCases.ok ? (listedCases.value?.cases ?? []) : [];
    }

    host.innerHTML = browserHTML({ projects, selected, cases });

    for (const button of host.querySelectorAll<HTMLButtonElement>('[data-project]')) {
      button.addEventListener('click', () => {
        const id = button.dataset['project'];
        if (id === undefined) return;
        deps.selectProject(id);
        void render(id);
      });
    }

    for (const button of host.querySelectorAll<HTMLButtonElement>('[data-case]')) {
      button.addEventListener('click', () => {
        const id = button.dataset['case'];
        if (id !== undefined) deps.openCase(id);
      });
    }

    for (const button of host.querySelectorAll<HTMLButtonElement>('[data-delete]')) {
      button.addEventListener('click', () => {
        const id = button.dataset['delete'];
        const title = button.dataset['title'] ?? '';
        if (id === undefined) return;
        if (!deps.confirm(deleteConfirmMessage(title))) return;

        void deps.deleteCase(id).then(() => render(selectedId));
      });
    }

    host.querySelector<HTMLFormElement>('#p2-newproject')?.addEventListener('submit', () => {
      const input = host.querySelector<HTMLInputElement>('#p2-projectname');
      const name = input?.value.trim() ?? '';
      if (name === '') return;

      void deps.createProject(name).then(() => render(selectedId));
    });
  }

  return { render };
}
